/**
 * Sprint A · Historia A1 — vista de auditoría cerrada.
 *
 * Ruta: /assessments/:id/closed
 *
 * Lee status del assessment; si !== 'completed' redirige a la vista
 * de captura (vista in-progress). En completed muestra 3 tabs:
 *   - Resumen   → GET /api/v1/assessments/:id/summary
 *   - Hallazgos → GET /api/v1/findings?assessment_id=:id (paginado)
 *   - Reportes  → GET /api/v1/assessments/:id/reports + botón generar
 *
 * No usa React Query: el repo no lo tiene como dependencia (decisión
 * de no añadir libs nuevas). Se mantiene el patrón useState/useEffect
 * que ya usan el resto de páginas.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { useAppShell } from '../components/AppShell.js';
import { ShieldAlert, AlertTriangle, Info, CheckCircle2, Loader2, Wand2 } from 'lucide-react';

import type { AssessmentSummary } from '@assessos/shared';

import { assessmentsApi, type AssessmentDetail } from '../api/assessments.js';
import { assessmentSummaryApi } from '../api/assessment-summary.js';
import {
  findingsApi,
  type FindingSeverity,
  type FindingSummary as Finding,
} from '../api/findings.js';
import {
  aiErrorMessage,
  downloadReport,
  reportLabel,
  reportsApi,
  type ReportItem,
} from '../api/reports.js';
import {
  Badge as LegacyBadge,
  type BadgeTone,
  ButtonLink,
  Card,
  PageHeader,
  // EmptyState y LoadingSkeleton legacy NO se importan — migrados a E1 SH-2.
} from '../components/ui.js';
import { Badge } from '../components/ui/Badge.js';
import { Button } from '../components/ui/Button.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton.js';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/Tabs.js';

type TabKey = 'summary' | 'findings' | 'reports';

// SEVERITY_TONE/SEVERITY_LABEL mantenidos para SummaryTab (usa LegacyBadge)
const SEVERITY_TONE: Record<FindingSeverity, BadgeTone> = {
  critical: 'rose',
  high: 'amber',
  medium: 'amber',
  low: 'emerald',
};

const SEVERITY_LABEL: Record<FindingSeverity, string> = {
  critical: 'Crítico',
  high: 'Alto',
  medium: 'Medio',
  low: 'Bajo',
};

// Config E1 para FindingsTab (Badge + ícono — WCAG 1.4.1)
const SEVERITY_E1_CONFIG: Record<
  FindingSeverity,
  { variant: 'danger' | 'warning' | 'info' | 'neutral'; icon: JSX.Element }
> = {
  critical: { variant: 'danger', icon: <ShieldAlert /> },
  high: { variant: 'warning', icon: <AlertTriangle /> },
  medium: { variant: 'info', icon: <Info /> },
  low: { variant: 'neutral', icon: <CheckCircle2 /> },
};

function fmtDateTime(s: string | null): string {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleString('es-DO', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function AssessmentClosedPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setBackTo, setBreadcrumb } = useAppShell();
  const [tab, setTab] = useState<TabKey>('summary');

  // Estado del assessment para el gate de status.
  const [assessment, setAssessment] = useState<AssessmentDetail | null>(null);
  const [assessmentError, setAssessmentError] = useState<string | null>(null);
  const [assessmentLoading, setAssessmentLoading] = useState(true);

  // Back button + breadcrumb (BB-UX-01 + BB-UX-03)
  useEffect(() => {
    if (!assessment?.site_id) return;
    setBackTo({ href: `/sites/${assessment.site_id}/assessments`, label: 'Histórico' });
    return () => setBackTo(null);
  }, [setBackTo, assessment?.site_id]);

  useEffect(() => {
    if (!assessment) return;
    const dateLabel = assessment.completed_at
      ? new Date(assessment.completed_at).toLocaleDateString('es-DO')
      : (id?.slice(0, 8) ?? 'Auditoría');
    setBreadcrumb([
      { label: 'Clientes', href: '/clients' },
      { label: 'Histórico', href: `/sites/${assessment.site_id}/assessments` },
      { label: dateLabel },
    ]);
    return () => setBreadcrumb([]);
  }, [setBreadcrumb, assessment, id]);

  useEffect(() => {
    if (!id) return;
    setAssessmentLoading(true);
    assessmentsApi
      .get(id)
      .then((a) => setAssessment(a))
      .catch((err: unknown) =>
        setAssessmentError(err instanceof Error ? err.message : 'Error cargando assessment'),
      )
      .finally(() => setAssessmentLoading(false));
  }, [id]);

  if (!id) {
    return <Navigate to="/clients" replace />;
  }
  if (assessmentLoading) {
    return (
      <main className="mx-auto max-w-6xl px-6 py-8">
        {/* SH-2: E1 LoadingSkeleton variant="card" (migrado desde legacy) */}
        <LoadingSkeleton variant="card" rows={5} label="Cargando auditoría…" />
      </main>
    );
  }
  if (assessmentError) {
    return (
      <main className="mx-auto max-w-6xl px-6 py-8">
        {/* SH-2: E1 EmptyState variant="error" reemplaza bloque rosa inline */}
        <EmptyState
          variant="error"
          title="No se pudo cargar la auditoría"
          description={assessmentError}
        />
      </main>
    );
  }
  if (!assessment) {
    return <Navigate to="/clients" replace />;
  }
  // Si el assessment NO está completed, esta vista no aplica: redirigir
  // a la pantalla de captura (vista in-progress).
  if (assessment.status !== 'completed') {
    return <Navigate to={`/assessments/${id}/capture`} replace />;
  }

  return (
    <main className="mx-auto max-w-6xl px-6 py-8 space-y-6">
      <PageHeader
        eyebrow="AUDITORÍA CERRADA"
        title={`Assessment ${id.slice(0, 8)}`}
        subtitle={`Cierre: ${fmtDateTime(assessment.completed_at)}`}
        actions={
          <ButtonLink to={`/assessments/${id}/iso-matrix`} variant="ghost" size="sm">
            Matriz ISO
          </ButtonLink>
        }
      />

      {/* Radix Tabs: keyboard nav, aria-selected, aria-controls/labelledby automáticos */}
      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList variant="default" aria-label="Secciones de la auditoría cerrada">
          <TabsTrigger value="summary">Resumen</TabsTrigger>
          <TabsTrigger value="findings">Hallazgos</TabsTrigger>
          <TabsTrigger value="reports">Reportes</TabsTrigger>
        </TabsList>
        <TabsContent value="summary">
          <SummaryTab assessmentId={id} />
        </TabsContent>
        <TabsContent value="findings">
          <FindingsTab assessmentId={id} onOpen={(fid) => navigate(`/findings/${fid}`)} />
        </TabsContent>
        <TabsContent value="reports">
          <ReportsTab assessmentId={id} />
        </TabsContent>
      </Tabs>
    </main>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tab Resumen
// ─────────────────────────────────────────────────────────────────────

function SummaryTab({ assessmentId }: { assessmentId: string }): JSX.Element {
  const [summary, setSummary] = useState<AssessmentSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    assessmentSummaryApi
      .get(assessmentId)
      .then(setSummary)
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Error cargando resumen'),
      )
      .finally(() => setLoading(false));
  }, [assessmentId]);

  // SH-2: E1 LoadingSkeleton variant="card" (migrado desde legacy)
  if (loading) return <LoadingSkeleton variant="card" rows={3} label="Cargando resumen…" />;
  if (error) {
    // SH-2: E1 EmptyState variant="error" reemplaza bloque rosa inline
    return <EmptyState variant="error" title="No se pudo cargar el resumen" description={error} />;
  }
  if (!summary) return <></>;

  const sev = summary.findings_summary.by_severity;
  const cls = summary.findings_summary.by_classification;
  const st = summary.findings_summary.by_status;
  const total = summary.findings_summary.total;

  return (
    <div className="space-y-6">
      {/* 1. Metadata */}
      <Card title="Metadata" hint="Datos del cierre de auditoría">
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <Item label="Cliente" value={summary.client.name} />
          <Item label="Sitio" value={summary.site.name} />
          <Item label="Dirección" value={summary.site.address ?? '—'} />
          <Item label="Auditor" value={summary.auditor.email} />
          <Item
            label="Tipo"
            value={
              summary.assessment.audit_type === 'internal'
                ? 'Interna'
                : summary.assessment.audit_type === 'external'
                  ? 'Externa'
                  : summary.assessment.audit_type === 'certification'
                    ? 'Certificación'
                    : '—'
            }
          />
          <Item label="Alcance" value={summary.assessment.audit_scope ?? '—'} />
          <Item label="Inicio" value={fmtDateTime(summary.assessment.started_at)} />
          <Item label="Cierre" value={fmtDateTime(summary.assessment.completed_at)} />
        </dl>
      </Card>

      {/* 2. Sprint Y #6b — Estado de remediación (barra apilada) */}
      <Card title="Estado de remediación" hint={`Del total de ${String(total)} hallazgos`}>
        {total === 0 ? (
          <EmptyState variant="empty" title="Sin hallazgos" />
        ) : (
          <>
            {/* Barra apilada pure CSS — WCAG 1.4.1: no solo color, leyenda obligatoria */}
            <div
              className="flex h-4 rounded-full overflow-hidden w-full mb-3"
              role="img"
              aria-label={`Distribución por estado: ${String(st.closed)} cerrados, ${String(st.open)} abiertos, ${String(st.in_progress)} en curso, ${String(st.resolved)} resueltos, ${String(st.dismissed)} descartados`}
            >
              {/* Los segmentos de la barra son puramente decorativos dentro del contenedor
                  role="img": el aria-label del contenedor describe el conjunto completo.
                  Los divs individuales NO deben llevar aria-label (WCAG aria-prohibited-attr). */}
              {st.closed > 0 && (
                <div
                  className="bg-cumple-success-600"
                  style={{ width: `${String(Math.round((st.closed / total) * 100))}%` }}
                  aria-hidden="true"
                />
              )}
              {st.in_progress > 0 && (
                <div
                  className="bg-cumple-warning-500"
                  style={{ width: `${String(Math.round((st.in_progress / total) * 100))}%` }}
                  aria-hidden="true"
                />
              )}
              {st.resolved > 0 && (
                <div
                  className="bg-slate-400"
                  style={{ width: `${String(Math.round((st.resolved / total) * 100))}%` }}
                  aria-hidden="true"
                />
              )}
              {st.open > 0 && (
                <div
                  className="bg-cumple-danger-500"
                  style={{ width: `${String(Math.round((st.open / total) * 100))}%` }}
                  aria-hidden="true"
                />
              )}
              {st.dismissed > 0 && (
                <div
                  className="bg-cumple-gray-200"
                  style={{ width: `${String(Math.round((st.dismissed / total) * 100))}%` }}
                  aria-hidden="true"
                />
              )}
            </div>
            {/* Leyenda — texto obligatorio por WCAG 1.4.1 */}
            <div className="flex flex-wrap gap-3">
              {[
                { label: 'Cerrados', count: st.closed, color: 'bg-cumple-success-600' },
                { label: 'En curso', count: st.in_progress, color: 'bg-cumple-warning-500' },
                { label: 'Resueltos', count: st.resolved, color: 'bg-slate-400' },
                { label: 'Abiertos', count: st.open, color: 'bg-cumple-danger-500' },
                { label: 'Descartados', count: st.dismissed, color: 'bg-cumple-gray-200' },
              ].map(({ label, count, color }) => (
                <span key={label} className="flex items-center gap-1 text-xs text-slate-700">
                  <span className={`h-3 w-3 rounded-sm ${color}`} aria-hidden="true" />
                  {label}: {String(count)} ({String(Math.round((count / total) * 100))}%)
                </span>
              ))}
            </div>
          </>
        )}
      </Card>

      {/* 3. Hallazgos por severidad — migrado de KpiBlock legacy a Badge E1 */}
      <Card title={`Hallazgos por severidad · ${String(total)} total`}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {[
            {
              sev: 'critical',
              label: 'Críticos',
              count: sev.critical,
              danger: 'danger',
              border: 'border-cumple-danger-200',
              bg: 'bg-cumple-danger-50',
              text: 'text-cumple-danger-700',
            },
            {
              sev: 'high',
              label: 'Altos',
              count: sev.high,
              danger: 'warning',
              border: 'border-cumple-warning-200',
              bg: 'bg-cumple-warning-50',
              text: 'text-cumple-warning-700',
            },
            {
              sev: 'medium',
              label: 'Medios',
              count: sev.medium,
              danger: 'info',
              border: 'border-cumple-info-200',
              bg: 'bg-cumple-info-50',
              text: 'text-cumple-info-700',
            },
            {
              sev: 'low',
              label: 'Bajos',
              count: sev.low,
              danger: 'neutral',
              border: 'border-slate-200',
              bg: 'bg-slate-50',
              text: 'text-slate-700',
            },
          ].map(({ sev: s, label, count, border, bg, text }) => (
            <div
              key={s}
              className={`flex flex-col items-start gap-1 p-3 rounded-lg border ${border} ${bg}`}
            >
              <p className={`text-2xl font-bold ${text}`}>{String(count)}</p>
              <p className={`text-xs font-semibold ${text}`}>{label}</p>
            </div>
          ))}
        </div>

        {/* Por clasificación ISO 19011 */}
        <div className="text-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            Por clasificación ISO 19011
          </p>
          <ul className="space-y-1">
            <KpiRow label="NC mayores" value={cls.nc_major} />
            <KpiRow label="NC menores" value={cls.nc_minor} />
            <KpiRow label="Observaciones" value={cls.observation} />
            <KpiRow label="OFI" value={cls.ofi} />
          </ul>
        </div>
      </Card>

      {/* 4. Sprint Y #6b — Cumplimiento por dominio.
          Defensivo: backends anteriores al Sprint Y no emiten este campo;
          el fallback [] mantiene retrocompatibilidad con respuestas viejas. */}
      {(summary.compliance_by_requirement ?? []).length === 0 ? (
        <Card title="Cumplimiento por dominio">
          <EmptyState
            variant="empty"
            title="Sin datos de dominio"
            description="Esta plantilla no declara dominios de scoring o el assessment aún no tiene datos de puntuación."
          />
        </Card>
      ) : (
        <Card
          title="Cumplimiento por dominio"
          hint={`${String((summary.compliance_by_requirement ?? []).length)} dominios evaluados`}
        >
          <table className="w-full text-sm">
            <caption className="sr-only">Porcentaje de cumplimiento por dominio</caption>
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left py-2 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Dominio
                </th>
                <th className="text-left py-2 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Score
                </th>
                <th className="text-left py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Nivel
                </th>
              </tr>
            </thead>
            <tbody>
              {(summary.compliance_by_requirement ?? []).map((req) => {
                const pct = req.percent;
                const barColor =
                  pct >= 75
                    ? 'bg-cumple-success-600'
                    : pct >= 50
                      ? 'bg-cumple-warning-500'
                      : 'bg-cumple-danger-500';
                const badgeVariant: 'success' | 'warning' | 'danger' =
                  pct >= 75 ? 'success' : pct >= 50 ? 'warning' : 'danger';
                const badgeLabel =
                  req.status === 'not_applicable'
                    ? 'N/A'
                    : pct >= 75
                      ? 'Cumple'
                      : pct >= 50
                        ? 'Mejora requerida'
                        : 'No cumple';
                return (
                  <tr key={req.requirement_id} className="border-b border-slate-50">
                    <td className="py-2 pr-4 font-medium text-slate-800">
                      {req.requirement_label}
                    </td>
                    <td className="py-2 pr-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 rounded-full bg-cumple-gray-100">
                          <div
                            className={`h-2 rounded-full ${barColor}`}
                            style={{ width: `${String(pct)}%` }}
                            role="progressbar"
                            aria-valuenow={pct}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-label={`${req.requirement_label}: ${String(pct)}%`}
                          />
                        </div>
                        <span
                          className={`text-sm font-semibold ${barColor.replace('bg-', 'text-')}`}
                        >
                          {String(pct)}%
                        </span>
                      </div>
                    </td>
                    <td className="py-2">
                      {req.status === 'not_applicable' ? (
                        <Badge variant="neutral" size="sm">
                          N/A
                        </Badge>
                      ) : (
                        <Badge variant={badgeVariant} size="sm">
                          {badgeLabel}
                        </Badge>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* 5. Top 5 hallazgos */}
      {summary.top_findings.length > 0 && (
        <Card title="Top 5 hallazgos" hint="Ordenados por severidad y fecha de creación">
          <ul className="space-y-2">
            {summary.top_findings.map((f) => (
              <li key={f.id} className="flex items-start gap-3 text-sm">
                <LegacyBadge tone={SEVERITY_TONE[f.severity]}>
                  {SEVERITY_LABEL[f.severity]}
                </LegacyBadge>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-brand-900 truncate">{f.title}</p>
                  <p className="text-xs text-slate-500">
                    {f.classification.replace('_', ' ')} · {f.status} · Plazo:{' '}
                    {f.due_date ? fmtDateTime(f.due_date) : '—'}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* 6. Evidencia */}
      <Card title="Evidencia">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <KpiBlock
            tone="brand"
            label="Fotos del survey"
            value={summary.evidence_summary.photos_count}
          />
          <KpiBlock
            tone="emerald"
            label="Evidencia de remediación"
            value={summary.evidence_summary.finding_evidence_count}
          />
        </div>
      </Card>
    </div>
  );
}

function Item({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="text-brand-900">{value}</dd>
    </div>
  );
}

function KpiBlock({
  tone,
  label,
  value,
}: {
  tone: BadgeTone;
  label: string;
  value: number;
}): JSX.Element {
  const bg =
    tone === 'rose'
      ? 'bg-rose-50 text-rose-700'
      : tone === 'amber'
        ? 'bg-amber-50 text-amber-700'
        : tone === 'emerald'
          ? 'bg-emerald-50 text-emerald-700'
          : 'bg-brand-50 text-brand-700';
  return (
    <div className={`rounded-lg p-3 ${bg}`}>
      <p className="text-2xl font-bold leading-none">{String(value)}</p>
      <p className="text-xs mt-1">{label}</p>
    </div>
  );
}

function KpiRow({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <li className="flex items-center justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <span className="font-semibold text-brand-900">{String(value)}</span>
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tab Hallazgos — E3 H3.2 re-skin
//
// LÓGICA DE DATOS INTACTA: findingsApi.list(assessment_id).
// CAMBIO APROBADO: filtro de severidad pasó a cliente (useMemo),
//   sin re-fetch al cambiar chips. El fetch inicial trae todos los
//   hallazgos del assessment.
// ─────────────────────────────────────────────────────────────────────

function FindingsTab({
  assessmentId,
  onOpen,
}: {
  assessmentId: string;
  onOpen: (id: string) => void;
}): JSX.Element {
  const [items, setItems] = useState<Finding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // severityFilter en cliente — NO provoca re-fetch
  const [severityFilter, setSeverityFilter] = useState<Set<FindingSeverity>>(new Set());

  // fetch solo depende de assessmentId (severidad filtrada en cliente)
  useEffect(() => {
    setLoading(true);
    findingsApi
      .list({ assessment_id: assessmentId })
      .then((r) => setItems(r.items))
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Error cargando hallazgos'),
      )
      .finally(() => setLoading(false));
  }, [assessmentId]);

  const toggleSeverity = useCallback((sev: FindingSeverity) => {
    setSeverityFilter((prev) => {
      const next = new Set(prev);
      if (next.has(sev)) next.delete(sev);
      else next.add(sev);
      return next;
    });
  }, []);

  // Filtrado cliente — instantáneo sin re-fetch (Gherkin H3.2)
  const filteredItems = useMemo(() => {
    if (severityFilter.size === 0) return items;
    return items.filter((f) => severityFilter.has(f.severity));
  }, [items, severityFilter]);

  const SEVERITIES: FindingSeverity[] = ['critical', 'high', 'medium', 'low'];

  return (
    <div className="space-y-4">
      {/* Chips de filtro de severidad — Button E1, aria-pressed — WCAG 2.1.1 / 4.1.2 */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-cumple-gray-700 uppercase tracking-wide">
          Severidad:
        </span>
        {SEVERITIES.map((sev) => {
          const active = severityFilter.has(sev);
          return (
            <Button
              key={sev}
              variant={active ? 'primary' : 'secondary'}
              size="sm"
              aria-pressed={active}
              onClick={() => toggleSeverity(sev)}
            >
              {SEVERITY_LABEL[sev]}
            </Button>
          );
        })}
        {/* Conteo dinámico aria-live — WCAG 1.3.1 + 4.1.3 */}
        {!loading && (
          <span
            aria-live="polite"
            aria-atomic="true"
            className="ml-auto text-sm font-semibold text-cumple-primary-700"
          >
            {filteredItems.length} de {items.length}
          </span>
        )}
      </div>

      <Card
        title={`Hallazgos${items.length > 0 ? ` · ${String(filteredItems.length)} de ${String(items.length)}` : ''}`}
        hint="Vista solo lectura. Para editar workflow, abre el detalle."
      >
        {/* SH-2: E1 LoadingSkeleton variant="list" — import-swap bajo riesgo */}
        {loading && <LoadingSkeleton variant="list" rows={4} label="Cargando hallazgos…" />}
        {/* SH-2: E1 EmptyState variant="error" reemplaza bloque rosa inline */}
        {error && (
          <EmptyState variant="error" title="Error al cargar hallazgos" description={error} />
        )}
        {/* SH-2: E1 EmptyState — distingue vacío real (sin filtro) de vacío por filtro activo */}
        {!loading && filteredItems.length === 0 && !error && severityFilter.size > 0 && (
          <EmptyState
            variant="filtered"
            title="Sin hallazgos con estos filtros"
            description="Ajusta los criterios de severidad."
            action={
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSeverityFilter(new Set())}
                className="mt-3"
              >
                Limpiar filtros
              </Button>
            }
          />
        )}
        {!loading && filteredItems.length === 0 && !error && severityFilter.size === 0 && (
          <EmptyState
            variant="empty"
            title="Sin hallazgos registrados"
            description="Esta auditoría no tiene hallazgos asociados."
          />
        )}
        {!loading && filteredItems.length > 0 && (
          <ul className="divide-y divide-slate-200" role="list">
            {filteredItems.map((f) => {
              const cfg = SEVERITY_E1_CONFIG[f.severity];
              return (
                // Fila ≤72px — min-h-[72px], flex items-center — UX spec H3.2
                <li key={f.id} className="flex items-center gap-3 py-3 min-h-[72px]">
                  {/* Badge E1 con ícono — WCAG 1.4.1 no solo color */}
                  <div className="w-20 shrink-0">
                    <Badge variant={cfg.variant} size="sm" leadingIcon={cfg.icon}>
                      {SEVERITY_LABEL[f.severity]}
                    </Badge>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-cumple-primary-900 truncate">
                      {f.question_text}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {f.classification.replace('_', ' ')} · {f.status}
                      {f.due_date ? ` · Plazo: ${fmtDateTime(f.due_date)}` : ''}
                    </p>
                  </div>
                  {/* "Ver →" Button ghost — min-h-[44px] por CVA base */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onOpen(f.id)}
                    aria-label={`Ver detalle de ${f.question_text}`}
                    className="shrink-0"
                  >
                    Ver →
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tab Reportes — E3 H3.4 re-skin
//
// LÓGICA INTACTA: reload, reportsApi.list/generate, polling tick() 3s/60s.
// CAMBIO: Button E1, Loader2, CheckCircle2, aria-busy/disabled.
// TODO(deuda-tecnica): tamaño de archivo no mostrado — ReportItem no expone
//   file_size_bytes. Requiere cambio de API/schema en backend para activarlo.
// ─────────────────────────────────────────────────────────────────────

function ReportsTab({ assessmentId }: { assessmentId: string }): JSX.Element {
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Sprint Z · ADR-031 — Estado del botón "Informe de cierre IA"
  const [generatingClosureIa, setGeneratingClosureIa] = useState(false);
  const [closureIaError, setClosureIaError] = useState<string | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    reportsApi
      .list(assessmentId)
      .then((r) => setReports(r.reports))
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Error cargando reportes'),
      )
      .finally(() => setLoading(false));
  }, [assessmentId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const hasFindingsReport = useMemo(
    () => reports.some((r) => r.kind === 'pdf_findings'),
    [reports],
  );

  const onGenerate = useCallback(async () => {
    setActionError(null);
    setGenerating(true);
    try {
      await reportsApi.generate(assessmentId, 'pdf_findings');
      // LÓGICA INTACTA: poll cada 3s hasta 20 intentos (60s) con setTimeout tick.
      let attempts = 0;
      const tick = async (): Promise<void> => {
        attempts += 1;
        const r = await reportsApi.list(assessmentId);
        setReports(r.reports);
        if (r.reports.some((x) => x.kind === 'pdf_findings')) {
          setGenerating(false);
          return;
        }
        if (attempts >= 20) {
          setGenerating(false);
          setActionError('El worker está tardando más de lo esperado. Recarga en unos minutos.');
          return;
        }
        setTimeout(() => void tick(), 3000);
      };
      setTimeout(() => void tick(), 3000);
    } catch (err: unknown) {
      setGenerating(false);
      setActionError(err instanceof Error ? err.message : 'Error encolando reporte');
    }
  }, [assessmentId]);

  // Sprint Z · ADR-031 — Genera informe de cierre IA y actualiza la lista de reportes.
  const onGenerateClosureIa = useCallback(async () => {
    setClosureIaError(null);
    setGeneratingClosureIa(true);
    try {
      await reportsApi.generate(assessmentId, 'ai_closure');
      let attempts = 0;
      const tick = async (): Promise<void> => {
        attempts += 1;
        try {
          const r = await reportsApi.list(assessmentId);
          setReports(r.reports);
          if (r.reports.some((x) => x.kind === 'ai_closure')) {
            setGeneratingClosureIa(false);
            return;
          }
        } catch {
          // Ignorar errores intermedios de polling
        }
        if (attempts >= 20) {
          setGeneratingClosureIa(false);
          setClosureIaError('ai_timeout');
          return;
        }
        setTimeout(() => void tick(), 3000);
      };
      setTimeout(() => void tick(), 3000);
    } catch (err: unknown) {
      setGeneratingClosureIa(false);
      const status = (err as { status?: number }).status;
      const message = (err as Error).message ?? '';
      if (status === 503 || message.includes('ai_not_configured')) {
        setClosureIaError('ai_not_configured');
      } else if (status === 429 || message.includes('ai_rate_limited')) {
        setClosureIaError('ai_rate_limited');
      } else {
        setClosureIaError('ai_upstream_unavailable');
      }
    }
  }, [assessmentId]);

  return (
    <Card
      title="Reportes generados"
      hint="Descarga los reportes ya generados. El de hallazgos se produce a demanda."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {/* Button E1 — aria-busy mientras genera — WCAG 4.1.2 */}
          <Button
            variant="primary"
            size="sm"
            onClick={() => void onGenerate()}
            disabled={generating}
            loading={generating}
            loadingText="Generando…"
            aria-busy={generating}
          >
            {generating
              ? 'Generando…'
              : hasFindingsReport
                ? 'Regenerar PDF de hallazgos'
                : 'Generar PDF de hallazgos'}
          </Button>
          {/* Sprint Z · ADR-031 — Botón "Informe de cierre IA" */}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void onGenerateClosureIa()}
            disabled={generatingClosureIa}
            loading={generatingClosureIa}
            loadingText="Generando…"
            aria-busy={generatingClosureIa}
            aria-label={
              generatingClosureIa
                ? 'Generando informe de cierre con IA, espera…'
                : 'Generar informe de cierre con IA'
            }
          >
            <Wand2 className="h-4 w-4" aria-hidden="true" />
            {generatingClosureIa ? 'Generando…' : 'Informe de cierre IA'}
          </Button>
        </div>
      }
    >
      {/* SH-2: E1 LoadingSkeleton variant="list" — import-swap bajo riesgo */}
      {loading && <LoadingSkeleton variant="list" rows={3} label="Cargando reportes…" />}
      {/* SH-2: E1 EmptyState variant="error" reemplaza bloque rosa inline */}
      {error && (
        <EmptyState
          variant="error"
          title="No se pudieron cargar los reportes"
          description={error}
          action={
            <Button variant="secondary" size="sm" onClick={reload} className="mt-3">
              Reintentar
            </Button>
          }
        />
      )}
      {/* actionError: alerta transitoria de acción — SE PRESERVA (no es estado de carga) */}
      {actionError && (
        <div
          role="alert"
          className="rounded bg-amber-50 border border-amber-200 p-3 text-sm text-amber-700 mb-3"
        >
          {actionError}
        </div>
      )}
      {/* Sprint Z · ADR-031 — Error del botón IA (role="alert" WCAG 4.1.3) */}
      {closureIaError && (
        <div
          role="alert"
          className="rounded bg-amber-50 border border-amber-200 p-3 text-sm text-amber-700 mb-3"
        >
          {aiErrorMessage(closureIaError)}
        </div>
      )}
      {/* SH-2: E1 EmptyState variant="empty" — sin reportes; CTA adyacente al contexto (spec H3.4) */}
      {!loading && reports.length === 0 && !error && (
        <EmptyState
          variant="empty"
          title="Sin reportes generados"
          description="Genera el reporte PDF de hallazgos para descargar el informe de esta auditoría."
          action={
            <Button
              variant="primary"
              size="sm"
              onClick={() => void onGenerate()}
              disabled={generating}
              loading={generating}
              loadingText="Generando…"
              aria-busy={generating}
              className="mt-3"
            >
              {generating ? 'Generando…' : 'Generar PDF de hallazgos'}
            </Button>
          }
        />
      )}
      {!loading && reports.length > 0 && (
        <ul className="divide-y divide-slate-200">
          {reports.map((r) => (
            <li key={r.id} className="py-3 flex items-center gap-3 min-h-[64px]">
              {/* role="status": anuncia "listo" a AT — WCAG 4.1.3 */}
              <span role="status" aria-label={`${reportLabel(r.kind)} listo`} className="sr-only" />
              <Loader2 className="h-5 w-5 text-cumple-success-600 opacity-0" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-cumple-primary-900">{reportLabel(r.kind)}</p>
                <p className="text-xs text-slate-500 font-mono break-all">
                  SHA-256: {r.sha256.slice(0, 16)}… · {fmtDateTime(r.generated_at)}
                </p>
              </div>
              {/* Descarga autenticada: fetch+Blob en lugar de <a download> que no
                  envía Authorization y recibe 401 JSON del backend. */}
              <Button
                variant="primary"
                size="sm"
                aria-label={`Descargar ${reportLabel(r.kind)}`}
                onClick={() => {
                  downloadReport(assessmentId, r.id, r.kind).catch((err: unknown) => {
                    setActionError(
                      err instanceof Error ? err.message : 'Error descargando reporte',
                    );
                  });
                }}
              >
                Descargar ↓
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
